import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, catchError, map, of, switchMap } from 'rxjs';
import { LeadService } from 'src/app/private/services/lead.service';
import { applyLeadVisibilityUserId } from 'src/app/utils/lead-visibility.util';
import { cloneLeadForEdit } from 'src/app/utils/lead-profile-field.util';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { MODULE_LOADING_MESSAGES } from 'src/app/private/services/module.config';

@Component({
  selector: 'app-lead-profile-page',
  templateUrl: './lead-profile-page.component.html',
  styleUrls: ['./lead-profile-page.component.scss'],
})
export class LeadProfilePageComponent implements OnInit, OnDestroy {
  editFieldValues: any = null;
  loading = true;
  notFound = false;
  private routeSub?: Subscription;

  // Loading message rotation fields
  activeMessages: string[] = [];
  currentMessageIndex = 0;
  currentMessage = '';
  fadeState: 'fade-out' | 'fade-in' | '' = '';
  private messageInterval: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private leadApi: LeadService,
    private localStorage: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.startMessageRotation();
    this.routeSub = this.route.paramMap
      .pipe(
        switchMap((params) => {
          const leadId = params.get('leadId');
          if (!leadId) {
            this.router.navigate(['/main/leads']);
            return of(null);
          }
          this.loading = true;
          this.startMessageRotation();
          this.notFound = false;
          this.editFieldValues = null;
          return this.fetchLead(leadId);
        })
      )
      .subscribe({
        next: (row) => {
          this.loading = false;
          this.cleanupMessageRotation();
          if (!row) {
            this.notFound = true;
            return;
          }
          this.editFieldValues = cloneLeadForEdit(row);
          if (this.editFieldValues?.fields == null) {
            this.editFieldValues.fields = {};
          }
        },
        error: () => {
          this.loading = false;
          this.cleanupMessageRotation();
          this.notFound = true;
        },
      });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.cleanupMessageRotation();
  }

  private startMessageRotation() {
    if (this.messageInterval) return; // Prevent multiple intervals
    this.activeMessages = MODULE_LOADING_MESSAGES['leads'] || MODULE_LOADING_MESSAGES['generic'] || [];
    this.currentMessageIndex = 0;
    this.currentMessage = this.activeMessages[this.currentMessageIndex] || 'Loading...';
    this.fadeState = '';

    this.messageInterval = setInterval(() => {
      this.fadeState = 'fade-out';
      setTimeout(() => {
        if (!this.loading) {
          this.cleanupMessageRotation();
          return;
        }
        this.currentMessageIndex = (this.currentMessageIndex + 1) % this.activeMessages.length;
        this.currentMessage = this.activeMessages[this.currentMessageIndex];
        this.fadeState = 'fade-in';
        setTimeout(() => {
          this.fadeState = '';
        }, 200);
      }, 200);
    }, 2000);
  }

  private cleanupMessageRotation() {
    if (this.messageInterval) {
      clearInterval(this.messageInterval);
      this.messageInterval = null;
    }
  }

  goBack(): void {
    this.router.navigate(['/main/leads']);
  }

  private fetchLead(leadId: string) {
    const isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole()
    );
    const param: Record<string, any> = {
      page: 0,
      limit: 1,
      where: {
        client_id: String(this.localStorage.getClientId()),
        center_id: String(this.localStorage.getCenterId()),
        lead_id: leadId,
      },
      order: { col: 'updatedAt', dir: 'DESC' },
    };

    if (!isAdmin) {
      param['where'] = {
        ...param['where'],
        center_id: String(this.localStorage.getCenterId()),
      };
    }

    applyLeadVisibilityUserId(
      param,
      this.localStorage.getUserId(),
      isAdmin,
      this.localStorage.getClientId()
    );

    return this.leadApi.getAllData(param).pipe(
      map((response: any) => response?.rows?.[0] ?? null),
      catchError(() => of(null))
    );
  }
}
